import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface NavigationRuntimeContextType {
  refreshNavigation: (menuName?: string) => void;
  isNavigationUpdating: boolean;
  lastUpdated: Date | null;
  preloadMenus: (menuNames: string[]) => void;
}

const NavigationRuntimeContext = createContext<NavigationRuntimeContextType>({
  refreshNavigation: () => {},
  isNavigationUpdating: false,
  lastUpdated: null,
  preloadMenus: () => {}
});

export const useNavigationRuntime = () => {
  const context = useContext(NavigationRuntimeContext);
  if (!context) {
    throw new Error('useNavigationRuntime must be used within NavigationRuntimeProvider');
  }
  return context;
};

interface NavigationRuntimeProviderProps {
  children: React.ReactNode;
  enableRealtimeSync?: boolean;
}

export const NavigationRuntimeProvider: React.FC<NavigationRuntimeProviderProps> = ({
  children,
  enableRealtimeSync = true
}) => {
  const [isNavigationUpdating, setIsNavigationUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const queryClient = useQueryClient();

  // Real-time sync with database (disabled on shared hosting - no WebSocket server)
  useEffect(() => {
    if (!enableRealtimeSync || !supabase) return;

    // Skip realtime on production shared hosting (no WebSocket support)
    const isSharedHosting = window.location.hostname === 'abascholding.com' || 
                            window.location.hostname === 'www.abascholding.com';
    if (isSharedHosting) return;

    const channel = supabase
      .channel('navigation-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'navigation_items'
        },
        (payload) => {
          console.log('Navigation item changed:', payload);
          setIsNavigationUpdating(true);
          
          queryClient.invalidateQueries({
            queryKey: ['navigation-menu']
          });
          
          setLastUpdated(new Date());
          
          setTimeout(() => {
            setIsNavigationUpdating(false);
          }, 1000);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'navigation_menus'
        },
        (payload) => {
          console.log('Navigation menu changed:', payload);
          setIsNavigationUpdating(true);
          
          queryClient.invalidateQueries({
            queryKey: ['navigation-menu']
          });
          
          setLastUpdated(new Date());
          
          setTimeout(() => {
            setIsNavigationUpdating(false);
          }, 1000);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enableRealtimeSync, queryClient]);

  const refreshNavigation = (menuName?: string) => {
    setIsNavigationUpdating(true);
    
    if (menuName) {
      queryClient.invalidateQueries({
        queryKey: ['navigation-menu', menuName]
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ['navigation-menu']
      });
    }
    
    setLastUpdated(new Date());
    
    setTimeout(() => {
      setIsNavigationUpdating(false);
    }, 500);
  };

  const preloadMenus = async (menuNames: string[]) => {
    for (const menuName of menuNames) {
      queryClient.prefetchQuery({
        queryKey: ['navigation-menu', menuName],
        staleTime: 5 * 60 * 1000 // 5 minutes
      });
    }
  };

  const value = {
    refreshNavigation,
    isNavigationUpdating,
    lastUpdated,
    preloadMenus
  };

  return (
    <NavigationRuntimeContext.Provider value={value}>
      {children}
    </NavigationRuntimeContext.Provider>
  );
};

// Hook for navigation state management in admin
export const useNavigationEditor = () => {
  const runtime = useNavigationRuntime();
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedMenus, setSelectedMenus] = useState<string[]>(['main-header']);

  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const addMenuToPreview = (menuName: string) => {
    if (!selectedMenus.includes(menuName)) {
      setSelectedMenus([...selectedMenus, menuName]);
      runtime.preloadMenus([menuName]);
    }
  };

  const removeMenuFromPreview = (menuName: string) => {
    setSelectedMenus(selectedMenus.filter(name => name !== menuName));
  };

  const refreshAllMenus = () => {
    selectedMenus.forEach(menuName => {
      runtime.refreshNavigation(menuName);
    });
  };

  return {
    ...runtime,
    isPreviewMode,
    togglePreviewMode,
    selectedMenus,
    addMenuToPreview,
    removeMenuFromPreview,
    refreshAllMenus
  };
};